import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import {
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../../constants/common'
import { EMAIL_SYMBOL_MAX_COUNT } from '../../../Email.constants'
import { parseEmailsFromInput } from '../../../Email.utils'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_MAX_FIELD_LENGTH,
  EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_SPECIAL_CHARACTERS,
  EMAIL_CREATION_FORM_RECIPIENTS_FIELD_LIMIT_NOTIFICATION,
  EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT,
  EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_LIMIT_NOTIFICATION,
  EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_MAX_COUNT,
  EMAIL_SPECIAL_CHARACTERS,
} from '../../EmailCreationForm.constants'
import { getEmailFormFlattenRecipients } from '../../EmailCreationForm.utils'

import {
  isContainsFieldLimit,
  isOverLimitRecipients,
  isToFieldLimit,
} from './useRecipientsFields.utils'

export function useRecipientsFields({
  collapsedHeader,
  mass,
  removeNotification,
  setNotification,
}) {
  const { t } = useTranslation()
  const { setFieldError, values } = useFormikContext()

  const ccValue = values[EMAIL_CREATION_FORM_FIELD_NAME_CC]
  const bccValue = values[EMAIL_CREATION_FORM_FIELD_NAME_BCC]

  const [initShowMoreValue, setInitShowMoreValue] = useState(true)
  const [collapsed, setCollapsed] = useState(
    () => collapsedHeader && getEmailFormFlattenRecipients(values).length > 1
  )
  const [showCcField, setShowCcField] = useState(ccValue.length > 0)
  const [showBccField, setShowBccField] = useState(bccValue.length > 0)

  function handleShowCcField() {
    return setShowCcField(true)
  }

  function handleShowBccField() {
    return setShowBccField(true)
  }

  function handleExpand() {
    return setCollapsed(false)
  }

  // Reveal the CC field when it gains values — during render (initial state
  // above already reflects the current values, so this handles later changes).
  const [prevCcValue, setPrevCcValue] = useState(ccValue)

  if (ccValue !== prevCcValue) {
    setPrevCcValue(ccValue)

    if (ccValue.length > 0 && !showCcField) {
      setShowCcField(true)
    }
  }

  // Reveal the BCC field when it gains values — during render.
  const [prevBccValue, setPrevBccValue] = useState(bccValue)

  if (bccValue !== prevBccValue) {
    setPrevBccValue(bccValue)

    if (bccValue.length > 0 && !showBccField) {
      setShowBccField(true)
    }
  }

  // Hide empty optional fields when the header collapses — during render.
  const [prevHideTrigger, setPrevHideTrigger] = useState({
    bcc: bccValue,
    cc: ccValue,
    collapsedHeader,
  })

  if (
    collapsedHeader !== prevHideTrigger.collapsedHeader ||
    ccValue !== prevHideTrigger.cc ||
    bccValue !== prevHideTrigger.bcc
  ) {
    setPrevHideTrigger({ bcc: bccValue, cc: ccValue, collapsedHeader })

    if (collapsedHeader) {
      if (ccValue.length === 0 && showCcField) {
        setShowCcField(false)
      }

      if (bccValue.length === 0 && showBccField) {
        setShowBccField(false)
      }
    }
  }

  const overLimitTotal = isOverLimitRecipients(values, mass)

  const overLimitField = isContainsFieldLimit(values, mass)

  useEffect(() => {
    if (overLimitTotal) {
      setNotification({
        id: EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_LIMIT_NOTIFICATION,
        text: t('Error.RecipientsTotalLimit', {
          limit: EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_MAX_COUNT,
        }),
        type: NOTIFICATION_TYPE.error,
      })
    } else {
      removeNotification(
        EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_LIMIT_NOTIFICATION
      )
    }

    if (overLimitField) {
      setNotification({
        id: EMAIL_CREATION_FORM_RECIPIENTS_FIELD_LIMIT_NOTIFICATION,
        text: t('Error.RecipientsFieldLimitKey', {
          limit: EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT,
        }),
        type: NOTIFICATION_TYPE.error,
      })
    } else {
      removeNotification(
        EMAIL_CREATION_FORM_RECIPIENTS_FIELD_LIMIT_NOTIFICATION
      )
    }

    if (
      values[EMAIL_CREATION_FORM_FIELD_NAME_TO] &&
      (overLimitTotal ||
        isToFieldLimit(values[EMAIL_CREATION_FORM_FIELD_NAME_TO], mass))
    ) {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_TO, true)
    } else {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_TO, null)
    }

    if (
      values[EMAIL_CREATION_FORM_FIELD_NAME_CC] &&
      (overLimitTotal ||
        isToFieldLimit(values[EMAIL_CREATION_FORM_FIELD_NAME_CC], mass))
    ) {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_CC, true)
    } else {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_CC, null)
    }

    if (
      values[EMAIL_CREATION_FORM_FIELD_NAME_BCC] &&
      (overLimitTotal ||
        isToFieldLimit(values[EMAIL_CREATION_FORM_FIELD_NAME_BCC], mass))
    ) {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_BCC, true)
    } else {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_BCC, null)
    }
  }, [overLimitField, overLimitTotal])

  // Collapse the recipients block based on the form values — during render
  // (initial `collapsed` above is derived from the same condition).
  const toValue = values[EMAIL_CREATION_FORM_FIELD_NAME_TO]
  const [prevCollapseTrigger, setPrevCollapseTrigger] = useState({
    bcc: bccValue,
    cc: ccValue,
    collapsedHeader,
    to: toValue,
  })

  if (
    collapsedHeader !== prevCollapseTrigger.collapsedHeader ||
    bccValue !== prevCollapseTrigger.bcc ||
    ccValue !== prevCollapseTrigger.cc ||
    toValue !== prevCollapseTrigger.to
  ) {
    setPrevCollapseTrigger({
      bcc: bccValue,
      cc: ccValue,
      collapsedHeader,
      to: toValue,
    })

    if (
      !collapsed &&
      collapsedHeader &&
      getEmailFormFlattenRecipients(values).length > 1
    ) {
      setCollapsed(true)
    } else {
      setCollapsed(false)
    }
  }

  function validateEmailInput(inputValue = EMPTY_STRING) {
    const emails = parseEmailsFromInput(inputValue)

    let hasLengthError = false
    let hasSpecialCharError = false

    emails.forEach(email => {
      if (email.length >= EMAIL_SYMBOL_MAX_COUNT) {
        hasLengthError = true
      }

      if (EMAIL_SPECIAL_CHARACTERS.test(email)) {
        hasSpecialCharError = true
      }
    })

    if (hasLengthError) {
      setNotification({
        id: EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_MAX_FIELD_LENGTH,
        text: t('Error.RecipientsInputLengthKey'),
        type: NOTIFICATION_TYPE.error,
      })
    } else {
      removeNotification(
        EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_MAX_FIELD_LENGTH
      )
    }

    if (hasSpecialCharError) {
      setNotification({
        id: EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_SPECIAL_CHARACTERS,
        text: t('Error.RecipientsInputIncludesSpecialCharactersKey'),
        type: NOTIFICATION_TYPE.error,
      })
    } else {
      removeNotification(
        EMAIL_CREATION_FORM_NOTIFICATION_RECIPIENTS_SPECIAL_CHARACTERS
      )
    }
  }

  return {
    collapsed,
    handleExpand,
    handleShowBccField,
    handleShowCcField,
    initShowMoreValue,
    setInitShowMoreValue,
    showBccField,
    showCcField,
    validateEmailInput,
  }
}
